import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { RTL_LANGUAGES } from '@klareospace/pharos-lib-front-utils';
import { cn } from '../../../lib/utils';
import { Progress } from '../../ui/progress';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '../../ui/sheet';
import { SideDrawerEscapeProvider, useSideDrawerEscapeInterceptorRef } from './side-drawer-escape-context';

/** Thin progress bar rendered flush at the top edge of a SideDrawer header, reflecting stage completion. */
export interface SideDrawerHeaderProgress {
  /** Accessible label for the progress bar (caller-provided — this component is i18n-agnostic) */
  ariaLabel: string;
  /** Total number of stages */
  max: number;
  /** Number of completed stages */
  value: number;
}

export interface SideDrawerProps {
  /** Whether to auto-focus the first focusable element when the drawer opens (default: true) */
  autoFocus?: boolean;
  /** Body content rendered between header and footer */
  children: React.ReactNode;
  /** Additional CSS classes for the SheetContent */
  className?: string;
  /**
   * Whether clicking outside the drawer (on the overlay) closes it (default: true).
   * Set to `false` for drawers that contain forms where accidental dismissal would
   * discard unsaved user input — the drawer will still close via Escape key, the
   * close "X" button, and any explicit Cancel / Submit actions.
   */
  dismissOnOutsideClick?: boolean;
  /** Footer content (typically action buttons) */
  footer?: React.ReactNode;
  /** Optional action element rendered in the header, positioned on the end side (same side as the close icon, below it) */
  headerAction?: React.ReactNode;
  /**
   * Custom header content rendered in place of the built-in title/subtitle header.
   * When provided, `title` is still required and rendered as a visually-hidden
   * `SheetTitle` (Radix a11y requirement). `subtitle` and `headerAction` are ignored.
   */
  headerContent?: React.ReactNode;
  /**
   * Thin progress bar rendered flush at the top edge of the drawer, reflecting stage completion.
   * When provided, the header region also gets a card/white background with a bottom border
   * (instead of flowing into the body like the default header). Omit for drawers with no staged
   * progress — existing consumers that do not pass this prop are unaffected.
   */
  headerProgress?: SideDrawerHeaderProgress;
  /** Callback when open state changes (close on Escape, close icon, or overlay click when dismissOnOutsideClick is true) */
  onOpenChange: (open: boolean) => void;
  /** Controlled open state */
  open: boolean;
  /** Subtitle shown below the title */
  subtitle?: string;
  /** Title shown in the header — always required (used as accessible name even when headerContent is provided) */
  title: string;
  /**
   * CSS class controlling the max-width of the drawer panel.
   * Defaults to `'sm:max-w-2xl'`. Pass e.g. `'sm:max-w-4xl'` for wider drawers.
   * Existing consumers that do not pass this prop are unaffected.
   */
  widthClassName?: string;
}

export function SideDrawer({
  autoFocus = true,
  children,
  className,
  dismissOnOutsideClick = true,
  footer,
  headerAction,
  headerContent,
  headerProgress,
  onOpenChange,
  open,
  subtitle,
  title,
  widthClassName = 'sm:max-w-2xl',
}: SideDrawerProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const isRtl = (RTL_LANGUAGES as readonly string[]).includes(i18n.language);
  const side = isRtl ? 'left' : 'right';
  const { consumeEscapeInterceptor, registerEscapeInterceptor } = useSideDrawerEscapeInterceptorRef();

  function renderHeader(): React.ReactNode {
    if (headerContent != null) {
      return (
        <>
          <SheetTitle className="sr-only">{title}</SheetTitle>
          {headerContent}
        </>
      );
    }
    return (
      <SheetHeader className={cn('flex flex-row items-start justify-between gap-2 pe-8', !headerProgress && 'pt-2')}>
        <div className="flex flex-col space-y-2 text-start">
          <SheetTitle>{title}</SheetTitle>
          {subtitle && <SheetDescription>{subtitle}</SheetDescription>}
        </div>
        {headerAction && <div className="shrink-0">{headerAction}</div>}
      </SheetHeader>
    );
  }

  function handleOpenAutoFocus(event: Event): void {
    if (!autoFocus) {
      event.preventDefault();
    }
  }

  // Focus restoration from a closing sibling dialog (e.g. task detail popin) must never dismiss this drawer.
  function handleFocusOutside(event: Event): void {
    event.preventDefault();
  }

  // A pointer-down inside another Radix dialog layer isn't an "outside" click on this drawer.
  function isInsideAnotherDialogLayer(event: Event): boolean {
    const target = event.target;
    if (!(target instanceof Element)) return false;
    return target.closest('[role="dialog"]') !== null;
  }

  function handlePointerDownOutside(event: Event): void {
    if (isInsideAnotherDialogLayer(event) || !dismissOnOutsideClick) {
      event.preventDefault();
    }
  }

  // Radix's DismissableLayer listens for Escape on `document` in the capture phase — ahead of any
  // bubble-phase handler inside the drawer — so a descendant (e.g. a field mid-edit) must claim the
  // key here, not by trying to stop the event from its own handler.
  function handleEscapeKeyDown(event: KeyboardEvent): void {
    if (consumeEscapeInterceptor()) {
      event.preventDefault();
    }
  }

  return (
    <Sheet onOpenChange={onOpenChange} open={open}>
      <SheetContent
        className={cn('flex flex-col h-full', widthClassName, className)}
        onEscapeKeyDown={handleEscapeKeyDown}
        onFocusOutside={handleFocusOutside}
        onOpenAutoFocus={handleOpenAutoFocus}
        onPointerDownOutside={handlePointerDownOutside}
        side={side}
      >
        <SideDrawerEscapeProvider registerEscapeInterceptor={registerEscapeInterceptor}>
          {headerProgress ? (
            <div className="-mx-6 -mt-6 flex flex-col">
              <Progress
                aria-label={headerProgress.ariaLabel}
                className="h-1 w-full rounded-none bg-muted"
                max={headerProgress.max}
                value={headerProgress.value}
              />
              <div className="border-b bg-card px-5 pb-3 pt-4">{renderHeader()}</div>
            </div>
          ) : (
            renderHeader()
          )}
          <div className="-mx-1 flex-1 overflow-y-auto px-1 py-4">{children}</div>
          {footer && <SheetFooter className="sm:justify-start">{footer}</SheetFooter>}
        </SideDrawerEscapeProvider>
      </SheetContent>
    </Sheet>
  );
}
